import { useState } from 'react';

import { Box, Link, Typography, TextField, Tooltip, SxProps, Theme } from '@mui/material';

import { InfoModal } from '../../atoms/InfoModal';
import { AppIcon } from '../../designSystem/icons/AppIcon';

type Source = Readonly<{
  name: string;
  url: string;
}>;

type Feedback = Readonly<{
  url: string;
  label: string;
  subject?: string;
}>;

type SourcesModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  description: string;
  sources: readonly Source[];
  feedback?: Feedback;
}>;

export const SourcesModal = ({
  open,
  onClose,
  description,
  sources,
  feedback,
}: SourcesModalProps) => {
  return (
    <InfoModal
      open={open}
      width={false}
      onClose={onClose}
      dialogSx={dialogSx}
      icon="FileAttachment02"
      hideCircleImage
      headerContent={
        <Box sx={headerContentSx}>
          <Typography
            variant="text-lg-semibold"
            color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
          >
            Sources
          </Typography>
          <Typography
            variant="text-sm-regular"
            color={({ backrs }) => backrs.colors['Colors/Text/text-secondary']}
          >
            {description}
          </Typography>
        </Box>
      }
      bodyContent={
        <Box sx={bodyContainerSx}>
          {sources.map((source) => (
            <SourceRow key={source.name} source={source} />
          ))}
          {feedback && (
            <Link
              id="sources-modal-feedback-link"
              href={
                feedback.subject
                  ? `${feedback.url}?subject=${encodeURIComponent(feedback.subject)}`
                  : feedback.url
              }
              target="_blank"
              rel="noopener noreferrer"
              sx={feedbackLinkSx}
            >
              <Typography variant="text-sm-medium">{feedback.label}</Typography>
            </Link>
          )}
        </Box>
      }
    />
  );
};

const SourceRow = ({ source }: { source: Source }) => {
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const slug = source.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(source.url);
      setShowCopySuccess(true);
      setTimeout(() => setShowCopySuccess(false), 1500);
    } catch {
      // clipboard not available
    }
  };

  return (
    <Box>
      <Typography
        variant="text-sm-medium"
        color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
      >
        {source.name}
      </Typography>
      <Box sx={sourceLinkContainerSx}>
        <TextField
          id={`sources-modal-url-${slug}`}
          fullWidth
          value={source.url}
          InputProps={{ readOnly: true }}
        />
        <Tooltip
          open={showCopySuccess}
          title="Copied!"
          placement="top"
          arrow
          disableFocusListener
          disableHoverListener
          disableTouchListener
          componentsProps={{
            popper: {
              sx: copiedPopperSx,
            },
          }}
        >
          <Box id={`sources-modal-copy-${slug}`} sx={copyIconSx} onClick={handleCopy}>
            <AppIcon iconName="Copy01" diameter="20px" color="Colors/Foreground/fg-quaternary" />
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

const dialogSx: SxProps<Theme> = {
  '& .MuiDialog-paper': {
    maxWidth: '400px',
  },
};

const headerContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  position: 'relative',
  zIndex: 2,
});

const bodyContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const sourceLinkContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-md'],
  alignItems: 'center',
  mt: backrs.spacing['spacing-sm'],
});

const copyIconSx: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  ml: backrs.spacing['spacing-xs'],
});

const copiedPopperSx: SxProps<Theme> = {
  zIndex: 99999999999999,
};

const feedbackLinkSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  textDecorationColor: backrs.colors['Colors/Text/text-tertiary'],
  '&:hover': {
    color: backrs.colors['Colors/Text/text-secondary'],
  },
});
